import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { applyPageMetadata } from "../../lib/seo";
import "./happy-tomato.css";

export function HappyTomatoPage() {
  const frameRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    applyPageMetadata({
      title: "Happy Tomato: Water Flow | Steria House Games",
      description:
        "Mainkan Happy Tomato: Water Flow di Steria House. Teka-teki susun pipa air 20 stage menantang dengan maskot tomat lucu, pipa pengecoh, dan Kunci Jawaban interaktif.",
      canonicalPath: "/games/happy-tomato",
    });

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const handleReload = useCallback(() => {
    if (iframeRef.current) {
      // eslint-disable-next-line no-self-assign
      iframeRef.current.src = iframeRef.current.src;
    }
  }, []);

  const handleToggleFullscreen = useCallback(async () => {
    if (!document.fullscreenElement) {
      if (frameRef.current?.requestFullscreen) {
        await frameRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      }
    }
  }, []);

  return (
    <main className={`ht-game ${isFullscreen ? "is-fullscreen" : ""}`}>
      <div className="ht-frame" ref={frameRef}>
        <header className="ht-top">
          <div className="ht-brand-group">
            <Link to="/games" className="ht-btn ht-back-btn" title="Kembali ke Daftar Game">
              ← <span>Daftar Game</span>
            </Link>
            <Link to="/" className="ht-brand" title="Kembali ke Beranda Steria House">
              <span>✦ STERIA HOUSE</span>
              <b>/ HAPPY TOMATO</b>
            </Link>
          </div>

          <div className="ht-top-actions">
            <button
              type="button"
              className="ht-btn"
              onClick={handleReload}
              title="Muat ulang game"
            >
              🔄 <span>Reset</span>
            </button>
            <button
              type="button"
              className={`ht-btn ${isFullscreen ? "active" : ""}`}
              onClick={handleToggleFullscreen}
              title="Layar Penuh"
            >
              ⛶ <span>{isFullscreen ? "Kecilkan" : "Layar Penuh"}</span>
            </button>
            <a
              href="/games/happy-tomato/game.html"
              target="_blank"
              rel="noopener noreferrer"
              className="ht-btn"
              title="Buka langsung di tab baru"
            >
              ↗ <span>Tab Baru</span>
            </a>
          </div>
        </header>

        <div className="ht-stage-container">
          <iframe
            ref={iframeRef}
            src="/games/happy-tomato/game.html"
            className="ht-iframe"
            title="Happy Tomato: Water Flow"
            allow="autoplay; fullscreen; accelerometer; gyroscope; screen-wake-lock"
            loading="eager"
          />
        </div>

        <footer className="ht-bottom">
          <span>PUZZLE WATER FLOW <b>● 20 STAGES</b></span>
          <span>COCOS CREATOR 3.8.8 <b>● HTML5 WEBGL</b></span>
        </footer>
      </div>

      <section className="ht-info-card" aria-label="Informasi Game Happy Tomato">
        <h2>🍅 Tentang Happy Tomato: Water Flow</h2>
        <p>
          Game teka-teki logika santai di mana Anda menyusun jalur pipa air untuk menyiram tanaman tomat
          yang sedang kehausan. Putar pipa ke arah yang tepat sebelum kuota langkah habis!
        </p>
        <ul>
          <li><strong>20 Stage Progresif:</strong> Dari tingkat Mudah (grid 3x3), Menengah (4x4), Sulit (5x5), hingga Master Finale (7x7).</li>
          <li><strong>Pipa Pengecoh:</strong> Pipa dekoratif dan buntu yang menguji ketelitian strategi Anda.</li>
          <li><strong>Kunci Jawaban Interaktif:</strong> Fitur auto-solve dengan animasi langkah demi langkah yang menjamin setiap stage 100% dapat diselesaikan.</li>
          <li><strong>Audio & Efek Partikel:</strong> Efek sintetis Web Audio API prosedural dan semburan air yang memuaskan.</li>
        </ul>
      </section>
    </main>
  );
}
